import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { Button, Card, DangerZone } from '../ui';
import { ProcessorStatePanel, ProcessorStateDoc } from '../ProcessorStateBadge';
import { ProcessorAction, ProcessorEntry } from './types';

const scalarEntries = (obj: Record<string, unknown>) =>
  Object.entries(obj).filter(([, v]) => ['string', 'number', 'boolean'].includes(typeof v));

export const GenericProcessorPanel: React.FC<{
  entry: ProcessorEntry;
  state?: ProcessorStateDoc;
}> = ({ entry, state }) => {
  const { apiRequest } = useAuth();
  const queryClient = useQueryClient();
  const [config, setConfig] = useState<Record<string, string>>(() =>
    Object.fromEntries((entry.configInputs ?? []).map((c) => [c.key, c.defaultValue])));
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [lastActionResult, setLastActionResult] = useState<{ label: string; data: any } | null>(null);

  useEffect(() => { setLastActionResult(null); }, [entry.id]);

  const statusQuery = useQuery({
    queryKey: ['processor-status', entry.id],
    queryFn: () => apiRequest<any>({ url: entry.statusEndpoint!, method: 'GET' }),
    enabled: Boolean(entry.statusEndpoint),
    refetchInterval: 15000,
  });

  const runAction = async (action: ProcessorAction) => {
    if (action.confirm && !window.confirm(action.confirm)) return;
    setBusy(true); setMessage(null);
    try {
      const data = typeof action.body === 'function' ? action.body(config) : action.body;
      const res = await apiRequest<any>({
        url: action.endpoint, method: action.method ?? 'POST',
        ...(data !== undefined ? { data } : {}),
      });
      setMessage(res?.message || `${action.label}: done`);
      setLastActionResult(res && typeof res === 'object' ? { label: action.label, data: res } : null);
      if (action.refreshAfter !== false) {
        queryClient.invalidateQueries({ queryKey: ['processor-status', entry.id] });
      }
    } catch (err: any) {
      setMessage(err?.response?.data?.error || `${action.label} failed`);
    } finally { setBusy(false); }
  };

  const status = statusQuery.data;

  return (
    <Card title={entry.name} actions={
      <>
        {entry.enableEndpoint && (
          <Button size="sm" variant="secondary" disabled={busy}
                  onClick={() => runAction({ label: 'Enable', endpoint: entry.enableEndpoint! })}>Enable</Button>
        )}
        {entry.disableEndpoint && (
          <Button size="sm" variant="ghost" disabled={busy}
                  onClick={() => runAction({ label: 'Disable', endpoint: entry.disableEndpoint! })}>Disable</Button>
        )}
      </>
    }>
      <p className="pc-desc">{entry.description}</p>

      {status && (
        <dl className="pc-status">
          {scalarEntries(status).map(([k, v]) => (
            <React.Fragment key={k}><dt>{k}</dt><dd>{String(v)}</dd></React.Fragment>
          ))}
        </dl>
      )}

      {entry.configInputs && entry.configInputs.length > 0 && (
        <div className="pc-config">
          {entry.configInputs.map((c) => (
            <label key={c.key}>{c.label}
              <input type={c.type} value={config[c.key] ?? ''} aria-label={c.label}
                     onChange={(e) => setConfig({ ...config, [c.key]: e.target.value })} />
            </label>
          ))}
        </div>
      )}

      {entry.runActions && entry.runActions.length > 0 && (
        <div className="pc-actions">
          {entry.runActions.map((a) => (
            <Button key={a.label} variant={a.variant ?? 'secondary'} disabled={busy}
                    onClick={() => runAction(a)}>{a.label}</Button>
          ))}
        </div>
      )}

      {message && <p className="pc-desc">{message}</p>}

      {lastActionResult && scalarEntries(lastActionResult.data).length > 0 && (
        <div className="pc-action-result">
          <p className="pc-desc">{lastActionResult.label}</p>
          <dl className="pc-status">
            {scalarEntries(lastActionResult.data).map(([k, v]) => (
              <React.Fragment key={k}><dt>{k}</dt><dd>{String(v)}</dd></React.Fragment>
            ))}
          </dl>
        </div>
      )}

      {entry.hasStatePanel && <ProcessorStatePanel processorId={entry.id} state={state} />}

      {entry.dangerActions && entry.dangerActions.length > 0 && (
        <DangerZone>
          {entry.dangerActions.map((a) => (
            <Button key={a.label} variant="danger" size="sm" disabled={busy}
                    onClick={() => runAction(a)}>{a.label}</Button>
          ))}
        </DangerZone>
      )}
    </Card>
  );
};
